import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import venueService from '../services/venueService';
import fileService from '../services/fileService';
import { info, error as logError } from '../utils/logger';
import '../styles/edit-profile.css';

/**
 * Página dedicada para editar el perfil del usuario
 * Reemplaza el modal con una experiencia más cómoda
 */
const EditProfile = () => {
  const { user, updateExtendedProfile } = useAuth();
  const navigate = useNavigate();
  
  // Detectar si el usuario es un tutor
  const isTutor = user?.role === 'parent';
  
  const [formData, setFormData] = useState({
    // Información básica del usuario
    name: '',
    email: '',
    phone: '',
    bio: '',
    birthdate: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    emergencyContact: '',
    emergencyPhone: '',
    venue: '',
    avatar: '',

    // Información del perfil extendido (solo para usuarios no tutores)
    parentName: '',
    childName: '',
    childBirthDate: '',
    team: '',
    region: '',
    division: '',
    playingPosition: '',
    secondaryPosition: '',
    familyInstagram: '',
    hasEquipment: null,
    equipmentSize: '',
    jerseyName: '',
    howDidYouKnow: '',
    howDidYouKnowOther: '',
    recommendedBy: '',
    acceptedTerms: false,
  });
  
  const [venues, setVenues] = useState([]);
  const [loadingVenues, setLoadingVenues] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Cargar datos del usuario al montar el componente
  useEffect(() => {
    if (user) {
      const userData = {
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        birthdate: user.birthdate ? user.birthdate.split('T')[0] : '',
        address: user.address?.street || '',
        city: user.address?.city || '',
        postalCode: user.address?.postalCode || '',
        country: user.address?.country || '',
        emergencyContact: !isTutor ? (user.emergencyContact?.name || '') : '',
        emergencyPhone: !isTutor ? (user.emergencyContact?.phone || '') : '',
        venue: !isTutor ? (user.venue || '') : '',
        avatar: user.avatar || '',
      };

      // Solo agregar campos extendidos para usuarios no tutores
      if (!isTutor && user.extendedProfile) {
        const extended = user.extendedProfile;
        userData.parentName = extended.parentName || '';
        userData.childName = extended.childName || '';
        userData.childBirthDate = extended.childBirthDate ? extended.childBirthDate.split('T')[0] : '';
        userData.team = extended.team || '';
        userData.region = extended.region || '';
        userData.division = extended.division || '';
        userData.playingPosition = extended.playingPosition || '';
        userData.secondaryPosition = extended.secondaryPosition || '';
        userData.familyInstagram = extended.familyInstagram || '';
        userData.hasEquipment = extended.hasEquipment;
        userData.equipmentSize = extended.equipmentSize || '';
        userData.jerseyName = extended.jerseyName || '';
        userData.howDidYouKnow = extended.howDidYouKnow || '';
        userData.howDidYouKnowOther = extended.howDidYouKnowOther || '';
        userData.recommendedBy = extended.recommendedBy || '';
        userData.acceptedTerms = extended.acceptedTerms || false;
      }

      setFormData(userData);
    }
  }, [user, isTutor]);

  // Cargar sedes disponibles
  useEffect(() => {
    const loadVenues = async () => {
      try {
        setLoadingVenues(true);
        const response = await venueService.getVenues();
        setVenues(response.data || []);
      } catch (error) {
        logError('Error al cargar sedes', { error: error.message });
      } finally {
        setLoadingVenues(false);
      }
    };

    loadVenues();
  }, []);

  // Manejar cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Manejar selección de archivo de avatar
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, avatar: 'Por favor selecciona un archivo de imagen válido' }));
        return;
      }

      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, avatar: 'La imagen debe ser menor a 5MB' }));
        return;
      }

      setAvatarFile(file);
      setErrors(prev => ({ ...prev, avatar: '' }));

      // Crear preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Convertir avatar a base64 para guardarlo en el perfil
  const convertAvatarToBase64 = async () => {
    if (!avatarFile) return null;

    setUploadingAvatar(true);
    try {
      // Convertir el archivo a base64
      const base64 = await fileService.fileToBase64(avatarFile);
      return base64;
    } catch (error) {
      console.error('Error converting avatar to base64:', error);
      setErrors(prev => ({ ...prev, avatar: 'Error al procesar la imagen' }));
      return null;
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Componente de avatar por defecto simple
  const DefaultAvatar = () => (
    <div
      style={{
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        backgroundColor: '#ff0000',
        border: '3px solid #000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontSize: '24px',
        fontWeight: 'bold',
        fontFamily: 'Arial, sans-serif'
      }}
    >
      AVATAR
    </div>
  );

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    // Validaciones básicas
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'El teléfono es obligatorio';
    }

    // Validaciones para usuarios no tutores
    if (!isTutor) {
      if (!formData.parentName.trim()) {
        newErrors.parentName = 'El nombre del padre/madre es obligatorio';
      }
      if (!formData.childName.trim()) {
        newErrors.childName = 'El nombre del niño/a es obligatorio';
      }
      if (!formData.childBirthDate) {
        newErrors.childBirthDate = 'La fecha de nacimiento del niño/a es obligatoria';
      } else {
        const anoNac = parseInt(formData.childBirthDate.split('-')[0], 10);
        if (anoNac < 2015 || anoNac > 2022) {
          newErrors.childBirthDate = 'La fecha de nacimiento debe estar entre los años 2015 y 2022';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Convertir avatar a base64 si hay uno nuevo
      let avatarData = formData.avatar;
      if (avatarFile) {
        const base64Avatar = await convertAvatarToBase64();
        if (base64Avatar) {
          avatarData = base64Avatar;
        } else {
          // Si falla la conversión del avatar, no continuar
          setLoading(false);
          return;
        }
      }

      // Preparar datos para enviar
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        bio: formData.bio,
        birthdate: formData.birthdate || null,
        avatar: avatarData,
        address: {
          street: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          country: formData.country,
        },
      };

      // Solo agregar contacto de emergencia para usuarios no tutores
      if (!isTutor) {
        updateData.emergencyContact = {
          name: formData.emergencyContact,
          phone: formData.emergencyPhone,
        };
      }

      // Solo agregar sede para usuarios no tutores (futbolistas)
      if (!isTutor) {
        updateData.venue = formData.venue;
      }

      // Agregar perfil extendido solo para usuarios no tutores
      if (!isTutor) {
        updateData.extendedProfile = {
          parentName: formData.parentName,
          childName: formData.childName,
          childBirthDate: formData.childBirthDate || null,
          team: formData.team,
          region: formData.region,
          division: formData.division,
          playingPosition: formData.playingPosition,
          secondaryPosition: formData.secondaryPosition,
          familyInstagram: formData.familyInstagram,
          hasEquipment: formData.hasEquipment,
          equipmentSize: formData.equipmentSize,
          jerseyName: formData.jerseyName,
          howDidYouKnow: formData.howDidYouKnow,
          howDidYouKnowOther: formData.howDidYouKnowOther,
          recommendedBy: formData.recommendedBy,
          acceptedTerms: formData.acceptedTerms,
        };
      }

      await updateExtendedProfile(updateData);
      info('Perfil actualizado exitosamente');
      
      // Redirigir de vuelta al perfil
      navigate('/direct-profile');
      
    } catch (error) {
      logError('Error al actualizar el perfil', { error: error.message });
      setErrors({ submit: 'Error al actualizar el perfil. Por favor, inténtalo de nuevo.' });
    } finally {
      setLoading(false);
    }
  };

  // Manejar cancelación
  const handleCancel = () => {
    navigate('/direct-profile');
  };

  if (!user) {
    return (
      <div className="edit-profile-page">
        <div className="container">
          <div className="loading-message">
            <h2>Cargando...</h2>
            <p>Por favor espere mientras cargamos su información.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-profile-page">
      <div className="container">
        <div className="edit-profile-header">
          <h1>✏️ Editar Perfil</h1>
          <p>Actualiza tu información personal y preferencias</p>
        </div>

        <form onSubmit={handleSubmit} className="edit-profile-form">
          {errors.submit && (
            <div className="error-message">
              <span className="material-icons">error</span>
              {errors.submit}
            </div>
          )}

          {/* Información Personal */}
          <div className="form-section">
            <h2>👤 Información Personal</h2>

            {/* Avatar */}
            <div className="form-group avatar-section">
              <label>Foto de perfil</label>
              <div className="avatar-upload">
                <div className="avatar-preview">
                  {/* Mostrar avatar válido o avatar por defecto */}
                  {(avatarPreview && avatarPreview.startsWith('data:image/')) ||
                   (formData.avatar && formData.avatar.startsWith('data:image/')) ? (
                    <img
                      src={avatarPreview || formData.avatar}
                      alt="Avatar"
                      className="avatar-image"
                    />
                  ) : (
                    <DefaultAvatar />
                  )}
                  {uploadingAvatar && (
                    <div className="avatar-loading">
                      <span className="material-icons spinning">refresh</span>
                    </div>
                  )}
                </div>
                <div className="avatar-controls">
                  <input
                    type="file"
                    id="avatar"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="avatar" className="btn btn-secondary">
                    <span className="material-icons">photo_camera</span>
                    Cambiar foto
                  </label>
                  <small className="help-text">
                    Formatos: JPG, PNG, GIF. Máximo 5MB.
                  </small>
                </div>
              </div>
              {errors.avatar && <span className="field-error">{errors.avatar}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Nombre completo *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={errors.name ? 'error' : ''}
                  required
                />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  required
                />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Teléfono *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={errors.phone ? 'error' : ''}
                  required
                />
                {errors.phone && <span className="field-error">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="birthdate">Fecha de nacimiento</label>
                <input
                  type="date"
                  id="birthdate"
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="bio">Biografía</label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows="3"
                placeholder="Cuéntanos un poco sobre ti..."
              />
            </div>
          </div>

          {/* Información de Contacto de Emergencia - Solo para usuarios no tutores */}
          {!isTutor && (
            <div className="form-section">
              <h2>🚨 Contacto de Emergencia</h2>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="emergencyContact">Nombre del contacto</label>
                  <input
                    type="text"
                    id="emergencyContact"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    placeholder="Nombre completo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="emergencyPhone">Teléfono de emergencia</label>
                  <input
                    type="tel"
                    id="emergencyPhone"
                    name="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={handleInputChange}
                    placeholder="Número de teléfono"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Dirección */}
          <div className="form-section">
            <h2>📍 Dirección</h2>
            
            <div className="form-group">
              <label htmlFor="address">Dirección</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Calle y número"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="city">Ciudad</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Ciudad"
                />
              </div>

              <div className="form-group">
                <label htmlFor="postalCode">Código postal</label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  placeholder="Código postal"
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">País</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="País"
                />
              </div>
            </div>
          </div>

          {/* Sede - Solo para usuarios no tutores (futbolistas) */}
          {!isTutor && (
            <div className="form-section">
              <h2>🏟️ Sede</h2>

              <div className="form-group">
                <label htmlFor="venue">Sede preferida</label>
                <select
                  id="venue"
                  name="venue"
                  value={formData.venue}
                  onChange={handleInputChange}
                  disabled={loadingVenues}
                >
                  <option value="">Selecciona una sede</option>
                  {venues.map(venue => (
                    <option key={venue._id} value={venue._id}>
                      {venue.name} - {venue.location}
                    </option>
                  ))}
                </select>
                {loadingVenues && <span className="loading-text">Cargando sedes...</span>}
              </div>
            </div>
          )}

          {/* Información del Futbolista - Solo para usuarios no tutores */}
          {!isTutor && (
            <>
              <div className="form-section">
                <h2>⚽ Información del Futbolista</h2>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="parentName">Nombre del padre/madre *</label>
                    <input
                      type="text"
                      id="parentName"
                      name="parentName"
                      value={formData.parentName}
                      onChange={handleInputChange}
                      className={errors.parentName ? 'error' : ''}
                      required
                    />
                    {errors.parentName && <span className="field-error">{errors.parentName}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="childName">Nombre del niño/a *</label>
                    <input
                      type="text"
                      id="childName"
                      name="childName"
                      value={formData.childName}
                      onChange={handleInputChange}
                      className={errors.childName ? 'error' : ''}
                      required
                    />
                    {errors.childName && <span className="field-error">{errors.childName}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="childBirthDate">Fecha de nacimiento del niño/a *</label>
                    <input
                      type="date"
                      id="childBirthDate"
                      name="childBirthDate"
                      value={formData.childBirthDate}
                      onChange={handleInputChange}
                      className={errors.childBirthDate ? 'error' : ''}
                      min="2015-01-01"
                      max="2022-12-31"
                      required
                    />
                    {errors.childBirthDate && <span className="field-error">{errors.childBirthDate}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="team">Equipo</label>
                    <input
                      type="text"
                      id="team"
                      name="team"
                      value={formData.team}
                      onChange={handleInputChange}
                      placeholder="Nombre del equipo"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="playingPosition">Posición principal</label>
                    <select
                      id="playingPosition"
                      name="playingPosition"
                      value={formData.playingPosition}
                      onChange={handleInputChange}
                    >
                      <option value="">Selecciona una posición</option>
                      <option value="Portero">Portero</option>
                      <option value="Defensa">Defensa</option>
                      <option value="Centrocampista">Centrocampista</option>
                      <option value="Delantero">Delantero</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="secondaryPosition">Posición secundaria</label>
                    <select
                      id="secondaryPosition"
                      name="secondaryPosition"
                      value={formData.secondaryPosition}
                      onChange={handleInputChange}
                    >
                      <option value="">Selecciona una posición</option>
                      <option value="Portero">Portero</option>
                      <option value="Defensa">Defensa</option>
                      <option value="Centrocampista">Centrocampista</option>
                      <option value="Delantero">Delantero</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="familyInstagram">Instagram familiar</label>
                  <input
                    type="text"
                    id="familyInstagram"
                    name="familyInstagram"
                    value={formData.familyInstagram}
                    onChange={handleInputChange}
                    placeholder="@usuario_instagram"
                  />
                </div>
              </div>

              <div className="form-section">
                <h2>👕 Equipamiento</h2>
                
                <div className="form-group">
                  <label>¿Tiene equipamiento propio?</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="hasEquipment"
                        value="true"
                        checked={formData.hasEquipment === true}
                        onChange={() => setFormData(prev => ({ ...prev, hasEquipment: true }))}
                      />
                      <span>Sí</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="hasEquipment"
                        value="false"
                        checked={formData.hasEquipment === false}
                        onChange={() => setFormData(prev => ({ ...prev, hasEquipment: false }))}
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>

                {formData.hasEquipment === false && (
                  <div className="form-group">
                    <label htmlFor="equipmentSize">Talla de equipamiento</label>
                    <select
                      id="equipmentSize"
                      name="equipmentSize"
                      value={formData.equipmentSize}
                      onChange={handleInputChange}
                    >
                      <option value="">Selecciona una talla</option>
                      <option value="XS">XS</option>
                      <option value="S">S</option>
                      <option value="M">M</option>
                      <option value="L">L</option>
                      <option value="XL">XL</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="jerseyName">Nombre en la camiseta</label>
                  <input
                    type="text"
                    id="jerseyName"
                    name="jerseyName"
                    value={formData.jerseyName}
                    onChange={handleInputChange}
                    placeholder="Nombre para la camiseta"
                  />
                </div>
              </div>

              <div className="form-section">
                <h2>📢 ¿Cómo nos conociste?</h2>
                
                <div className="form-group">
                  <label htmlFor="howDidYouKnow">¿Cómo conociste ProKidsApp?</label>
                  <select
                    id="howDidYouKnow"
                    name="howDidYouKnow"
                    value={formData.howDidYouKnow}
                    onChange={handleInputChange}
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="Redes sociales">Redes sociales</option>
                    <option value="Recomendación">Recomendación</option>
                    <option value="Búsqueda en Google">Búsqueda en Google</option>
                    <option value="Publicidad">Publicidad</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>

                {formData.howDidYouKnow === 'Otro' && (
                  <div className="form-group">
                    <label htmlFor="howDidYouKnowOther">Especifica cómo nos conociste</label>
                    <input
                      type="text"
                      id="howDidYouKnowOther"
                      name="howDidYouKnowOther"
                      value={formData.howDidYouKnowOther}
                      onChange={handleInputChange}
                      placeholder="Cuéntanos cómo nos conociste"
                    />
                  </div>
                )}

                {formData.howDidYouKnow === 'Recomendación' && (
                  <div className="form-group">
                    <label htmlFor="recommendedBy">¿Quién te recomendó?</label>
                    <input
                      type="text"
                      id="recommendedBy"
                      name="recommendedBy"
                      value={formData.recommendedBy}
                      onChange={handleInputChange}
                      placeholder="Nombre de quien te recomendó"
                    />
                  </div>
                )}
              </div>

              <div className="form-section">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="acceptedTerms"
                      checked={formData.acceptedTerms}
                      onChange={handleInputChange}
                    />
                    <span>Acepto los términos y condiciones</span>
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Botones de acción */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="material-icons spinning">refresh</span>
                  Guardando...
                </>
              ) : (
                <>
                  <span className="material-icons">save</span>
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfile;
